import Swiper, { Autoplay, Navigation, Pagination } from 'swiper';
import { swiperObserver } from '../../utils/swiper.utils.js';

const TESTIMONIALS_WRAPPER_SELECTOR = '.testimonials-swiper-wr';
const QUOTE_SELECTOR = '.testimonial-card__quote';
const QUOTE_VISIBLE_LINES = 7;
const RESIZE_THROTTLE_DELAY = 120;
const getPixelValue = (value) => Number.parseFloat(value) || 0;

const getLineHeight = (styles) => {
  const lineHeight = getPixelValue(styles.lineHeight);

  return lineHeight || getPixelValue(styles.fontSize) * 1.2;
};

const getQuoteContentHeight = (content) => {
  let remainingLines = QUOTE_VISIBLE_LINES;
  let height = 0;

  Array.from(content.children).forEach((block) => {
    if (!remainingLines) {
      return;
    }

    const styles = window.getComputedStyle(block);
    const lineHeight = getLineHeight(styles);
    const verticalPaddingAndBorder = [
      styles.paddingTop,
      styles.paddingBottom,
      styles.borderTopWidth,
      styles.borderBottomWidth,
    ].reduce((total, value) => total + getPixelValue(value), 0);
    const textHeight = Math.max(0, block.offsetHeight - verticalPaddingAndBorder);
    const linesCount = Math.max(1, Math.round(textHeight / lineHeight));
    const visibleLines = Math.min(linesCount, remainingLines);
    const textOffset = block.offsetTop
      + getPixelValue(styles.paddingTop)
      + getPixelValue(styles.borderTopWidth);

    height = Math.max(height, textOffset + lineHeight * visibleLines);

    if (visibleLines === linesCount) {
      height += getPixelValue(styles.paddingBottom)
        + getPixelValue(styles.borderBottomWidth);
    }

    remainingLines -= visibleLines;
  });

  if (remainingLines === QUOTE_VISIBLE_LINES) {
    return Math.min(
      content.scrollHeight,
      getLineHeight(window.getComputedStyle(content)) * QUOTE_VISIBLE_LINES,
    );
  }

  return height;
};

const syncTestimonialQuoteHeight = (quote) => {
  const content = quote.querySelector('.simplebar-content');

  if (!content) {
    return false;
  }

  const simpleBar = quote.SimpleBar ?? window.SimpleBar?.instances?.get(quote);
  quote.style.maxHeight = 'none';
  simpleBar?.recalculate();

  const contentWrapper = quote.querySelector('.simplebar-content-wrapper');
  const viewportHeight = contentWrapper?.clientHeight ?? content.offsetHeight;
  const quoteChromeHeight = Math.max(0, quote.offsetHeight - viewportHeight);
  const contentHeight = getQuoteContentHeight(content);
  const maxHeight = Math.min(
    quote.offsetHeight,
    Math.ceil(quoteChromeHeight + contentHeight),
  );

  quote.style.maxHeight = `${maxHeight}px`;

  simpleBar?.recalculate();

  return true;
};

const syncTestimonialQuoteHeights = (swiper) => {
  if (swiper.destroyed) {
    return;
  }

  swiper.slides.forEach((slide) => {
    slide.querySelectorAll(QUOTE_SELECTOR).forEach(syncTestimonialQuoteHeight);
  });
};

const initTestimonialQuoteHeightSync = (swiper) => {
  let frameId = null;
  let resizeTimeoutId = null;

  const scheduleSync = () => {
    if (frameId !== null) {
      return;
    }

    frameId = window.requestAnimationFrame(() => {
      frameId = null;
      syncTestimonialQuoteHeights(swiper);
    });
  };
  const throttleResize = () => {
    if (resizeTimeoutId !== null) {
      return;
    }

    resizeTimeoutId = window.setTimeout(() => {
      resizeTimeoutId = null;
      scheduleSync();
    }, RESIZE_THROTTLE_DELAY);
  };

  swiper.on('imagesReady', scheduleSync);
  swiper.on('update', scheduleSync);
  swiper.on('observerUpdate', scheduleSync);
  swiper.on('breakpoint', scheduleSync);
  swiper.on('resize', throttleResize);
  window.addEventListener('load', scheduleSync, { once: true });
  window.addEventListener('resize', throttleResize, { passive: true });

  if (document.fonts?.ready) {
    document.fonts.ready.then(scheduleSync).catch(scheduleSync);
  }

  swiper.on('destroy', () => {
    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
    }

    if (resizeTimeoutId !== null) {
      window.clearTimeout(resizeTimeoutId);
    }

    window.removeEventListener('load', scheduleSync);
    window.removeEventListener('resize', throttleResize);
  });

  scheduleSync();
  window.requestAnimationFrame(scheduleSync);
};

const syncCharityTestimonialsBackground = (swiper) => {
  const section = swiper.el.closest('.testimonials-sec');

  if (!section) return;

  const background = section.querySelector('.testimonials-sec__bg.charity-type');
  const activeCard = swiper.el.querySelector('.swiper-slide-active .testimonial-card');

  if (!background || !activeCard) return;

  const sectionRect = section.getBoundingClientRect();
  const cardRect = activeCard.getBoundingClientRect();
  const top = cardRect.top - sectionRect.top + cardRect.height / 2;

  section.style.setProperty('--testimonials-charity-bg-top', `${top}px`);
};

const initCharityTestimonialsBackgroundSync = (swiper) => {
  const section = swiper.el.closest('.testimonials-sec');

  if (!section) return;

  const background = section.querySelector('.testimonials-sec__bg.charity-type');

  if (!background) return;

  let frameId = null;
  const scheduleSync = () => {
    if (frameId !== null) return;

    frameId = window.requestAnimationFrame(() => {
      frameId = null;
      syncCharityTestimonialsBackground(swiper);
    });
  };

  swiper.on('imagesReady', scheduleSync);
  swiper.on('slideChangeTransitionEnd', scheduleSync);
  window.addEventListener('load', scheduleSync, { once: true });
  window.addEventListener('resize', scheduleSync, { passive: true });
  swiper.on('destroy', () => {
    if (frameId !== null) window.cancelAnimationFrame(frameId);

    window.removeEventListener('load', scheduleSync);
    window.removeEventListener('resize', scheduleSync);
  });

  scheduleSync();
};

const getTestimonialsWrappers = (scope = document) => {
  const wrappers = Array.from(scope?.querySelectorAll?.(TESTIMONIALS_WRAPPER_SELECTOR) ?? []);

  if (scope?.matches?.(TESTIMONIALS_WRAPPER_SELECTOR)) {
    wrappers.unshift(scope);
  }

  return wrappers;
};

const initTestimonialsSwiper = (scope = document) => {
  getTestimonialsWrappers(scope).forEach((el) => {
    const swiperEl = el.querySelector('.swiper');

    if (!swiperEl || swiperEl.swiper) {
      return;
    }

    const nextEl = el.querySelector('.swiper-button-next');
    const prevEl = el.querySelector('.swiper-button-prev');
    const paginationEl = el.querySelector('.swiper-pagination');
    const swiperConfig = {
      modules: [Navigation, Pagination, Autoplay],
      observer: true,
      observeParents: true,
      slidesPerView: 1,
      spaceBetween: 14,
      centeredSlides: true,
      loop: true,
      loopedSlides: 10,
      loopAdditionalSlides: 4,
      speed: 400,
      threshold: 10,
      preventInteractionOnTransition: true,
      watchSlidesProgress: true,
      autoplay: {
        delay: 4000,
        disableOnInteraction: false,
        pauseOnMouseEnter: window.innerWidth >= 1033,
      },
      breakpoints: {
        641: {
          slidesPerView: 1.6,
          spaceBetween: 16,
        },
        768: {
          slidesPerView: 2.3,
          spaceBetween: 20,
        },
        992: {
          slidesPerView: 3,
          spaceBetween: 32,
        },
      },
      on: {
        init(swiper) {
          syncCharityTestimonialsBackground(swiper);
          initTestimonialQuoteHeightSync(swiper);
        },
        setTranslate(swiper) {
          swiper.slides.forEach((slide) => {
            const p = slide.progress;
            const translateY = p * p * 25;
            const rotate = -p * 4;
            slide.style.setProperty('--slide-translate-y', `${translateY}px`);
            slide.style.setProperty('--slide-rotate', `${rotate}deg`);
          });
        },
        setTransition(swiper, duration) {
          swiper.slides.forEach((slide) => {
            slide.style.transition = `transform ${duration}ms ease`;
          });
        },
        transitionEnd(swiper) {
          swiper.slides.forEach((slide) => {
            slide.style.transition = '';
          });
        },
      },
    };

    if (nextEl && prevEl) {
      swiperConfig.navigation = { nextEl, prevEl };
    }

    if (paginationEl) {
      swiperConfig.pagination = { el: paginationEl, clickable: true };
    }

    const swiper = new Swiper(swiperEl, swiperConfig);

    initCharityTestimonialsBackgroundSync(swiper);
    swiperObserver(swiper);
  });
};

initTestimonialsSwiper();
window.initTestimonialsSwiper = initTestimonialsSwiper;
